import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { CommandeClientsService } from "app/features/commande-client/services/commandes-client.service";
import { SnackService } from "app/shared/services/snack-service.service";
import { SessionExpireeService } from "app/shared/services/session-expiree.service";
import { TranslateUtils } from "app/shared/utils/translate.utils";
import { Observable, of, throwError } from "rxjs";
import { catchError } from "rxjs/operators";
import { AuthService } from "../features/auth/services/auth.service";
import { PingService } from "../shared/services/ping.service";
import { NetworkStatusService } from "../shared/services/network-status.service";

/** Méthodes qui ÉCRIVENT : leur échec sans réseau doit se dire (rien n'a été enregistré). */
const ECRITURES = new Set(["POST", "PUT", "PATCH", "DELETE"]);
/** Un seul « pas de réseau : rien n'a été enregistré » par rafale (plusieurs écritures d'un même geste). */
const NON_ENREGISTRE_MS = 4_000;

/** Code renvoyé par back-metier quand le compte a été désactivé. */
export const ACCOUNT_DISABLED_ERROR_CODE = "AUTH.ACCOUNT_DISABLED";

/**
 * Un 401 est une session expirée quand rien de plus précis n'est dit : pas de code métier traduit, et pas
 * une requête d'authentification (un mot de passe faux répond aussi 401, avec son propre message).
 */
export function estSessionExpiree(error: HttpErrorResponse, messageResolu: string | undefined): boolean {
  if (error.status !== 401) return false;
  if (/\/auth\//.test(error.url ?? "")) return false;
  const code = error?.error?.errorCode;
  if (code && messageResolu && messageResolu !== error?.error?.message) return false;
  return true;
}

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private error503inProgress: boolean = false;
  private accountDisabledInProgress = false;
  private nonEnregistreAt = 0;

  constructor(
    private authService: AuthService,
    private snackService: SnackService,
    private commandeClientService: CommandeClientsService,
    private pingService: PingService,
    private network: NetworkStatusService,
    private sessionExpiree?: SessionExpireeService,
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    let headers: HttpHeaders = req.headers;
    // ⚠️ Flag capturé PAR REQUÊTE (const locale), jamais sur `this` :
    // l'interceptor est un singleton et le `catchError` s'exécute de façon
    // asynchrone — une propriété d'instance était écrasée par chaque requête
    // concurrente (race), et surtout elle n'était JAMAIS LUE dans le
    // catchError : « skip-error-interceptor » n'a donc jamais silencié les
    // erreurs standard (bug historique, corrigé des deux côtés le 24/07).
    const skipErrorInterceptor = headers.has("skip-error-interceptor");
    headers = headers.delete("skip-error-interceptor");

    this.commandeClientService.resetState();

    return this.interceptHttpError(
      next,
      req.clone({ headers }),
      skipErrorInterceptor,
    );
  }

  /**
   * Vérifie si erreur HTTP interceptée (0 || 404) et affiche un toastr d'erreur.
   * @param {HttpHandler} next
   * @param {HttpRequest<any>} req
   * @returns {Observable<any>}
   */
  private interceptHttpError(
    next: HttpHandler,
    req: HttpRequest<any>,
    skipErrorInterceptor = false,
  ): Observable<any> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        // Requête marquée silencieuse (skip-error-interceptor) : aucun snack
        // ni règle globale — l'appelant affiche l'erreur dans son contexte
        // (ex. warning inline lots-disponibles). L'erreur remonte telle quelle.
        let message = this.resolveErrorMessage(error);

        const chunkFailedMessage = /Loading chunk [\d]+ failed/;

        // Hors ligne, recharger donnerait la page d'erreur du navigateur : on attend le réseau.
        if (chunkFailedMessage.test(message) && this.network.online()) {
          window.location.reload();
        }

        // Compte désactivé (back : login ET chaque requête, cf. back-metier
        // `auth/CLAUDE.md`) : déconnexion franche et retour à l'écran de
        // connexion. Traité AVANT le bypass `skip-error-interceptor`, comme le
        // 503 : c'est un cas système, une requête silencieuse ne doit pas
        // laisser l'utilisateur sur un écran qui ne charge plus rien.
        if (
          error.status === 401 &&
          error?.error?.errorCode === ACCOUNT_DISABLED_ERROR_CODE
        ) {
          this.handleAccountDisabled(message);
          return throwError(() => error);
        }

        // L'appelant gère lui-même l'affichage de l'erreur (ex. retry TOCTOU
        // 409/412 du rebind/apply-edit, où l'interceptor afficherait un snack
        // parasite par-dessus le warn de la facade). On relaie l'erreur sans
        // snack — SAUF les cas système globaux, qui restent gérés :
        //   - le rechargement de chunk, testé juste au-dessus ;
        //   - le 503, qui déclenche le logout et le debounce.
        //
        // ⚠️ Les deux branches ont corrigé ce bug indépendamment, mais SEULE
        // celle de `testing` excluait le 503. Sans cette exclusion, une requête
        // marquée `skip-error-interceptor` qui échoue en 503 ne déconnecte plus
        // l'utilisateur, et un chunk-load-failed marqué skip ne recharge plus la
        // page. Ne pas retirer la condition (réintégration du 10/08/2026).
        if (skipErrorInterceptor && error.status !== 503) {
          return throwError(() => error);
        }

        // L'appelant gère lui-même l'affichage de l'erreur (ex: retry TOCTOU
        // 409/412 du rebind/apply-edit, où l'interceptor afficherait un snack
        // d'erreur parasite par-dessus le warn de la facade). On relaie l'erreur
        // sans snack — sauf les cas système globaux (chunk reload ci-dessus,
        // 503 logout) qui restent gérés.
        if (skipErrorInterceptor && error.status !== 503) {
          return throwError(() => error);
        }

        if (req.responseType === "blob") {
          try {
            message = JSON.parse(error?.error?.text()).message;
          } catch (e) {
            message = error?.error?.text()?.toString() ?? error?.error?.text();
          }
        }

        switch (error.status) {
          //case 412:
          //  this.snackService.error(message);
          //  return of(undefined);
          case 502:
          case 504:
            // SERVEUR INJOIGNABLE derrière le relais (16/10/2026) : le corps est une page HTML, sans message.
            // Le snack sortait VIDE (« erreur non identifiée ») ; c'est la pastille du réseau qui le dit,
            // et une écriture dit qu'elle n'est pas partie, comme sans réseau.
            if (ECRITURES.has(req.method) && Date.now() - this.nonEnregistreAt > NON_ENREGISTRE_MS) {
              this.nonEnregistreAt = Date.now();
              this.snackService.error(TranslateUtils.translate("system.network.server_not_saved"));
            }
            return throwError(() => error);
          case 0:
            // PAS DE RÉSEAU (01/10/2026) : une ÉCRITURE qui n'est pas partie le DIT — avant, le geste
            // semblait sans effet (le bouton tournait, puis plus rien), et l'opérateur croyait sa
            // saisie enregistrée. Une lecture se tait : la pastille du réseau (`vl-network-status`)
            // dit déjà qu'il n'y en a plus. Le formulaire, lui, reste ouvert avec la saisie.
            if (ECRITURES.has(req.method) && Date.now() - this.nonEnregistreAt > NON_ENREGISTRE_MS) {
              this.nonEnregistreAt = Date.now();
              this.snackService.error(TranslateUtils.translate("system.network.not_saved"));
            }
            return throwError(() => error);
          case 500:
            this.traiteError(error);
            return throwError(error);
          case 503:
            if (this.error503inProgress) {
              return of(undefined);
            }
            this.error503inProgress = true;
            this.snackService.error(message);
            this.authService.logout();
            setTimeout(() => {
              this.error503inProgress = false;
            }, 1000);
            return of(undefined);
          case 401:
            // Session expirée (16/10/2026) : « Unauthorized » brut ne disait pas quoi faire. Un 401 sans
            // code métier traduit, hors écran de connexion (mot de passe faux), se dit en clair avec un
            // bouton « Se reconnecter ».
            if (estSessionExpiree(error, this.resolveErrorMessage(error))) {
              this.sessionExpiree?.signaler();
              return throwError(error);
            }
            this.traiteError(error);
            return throwError(error);
          case 404:
            this.snackService.error(message);
            return throwError(error);
          //return of(undefined);
          default:
            // Jamais un snack vide (16/10/2026) : sans message du serveur, on dit au moins ce qui s'est passé.
            this.snackService.error(message || TranslateUtils.translate("system.erreur_sans_message", { code: error.status }));
            return throwError(error);
        }
      }),
    );
  }

  /**
   * Résout le message d'erreur : tente de traduire l'errorCode du back,
   * sinon fallback sur le message brut.
   */
  private resolveErrorMessage(error: HttpErrorResponse): string {
    const errorCode = error?.error?.errorCode;
    const params = error?.error?.params;
    const fallback = error?.error?.message;

    if (errorCode) {
      const translated = TranslateUtils.translate(
        `errors.${errorCode}`,
        params,
      );
      // Si la traduction retourne la clé elle-même, c'est qu'elle n'existe pas
      if (translated && translated !== `errors.${errorCode}`) {
        return translated;
      }
    }

    return fallback;
  }

  /**
   * Toutes les requêtes en vol d'un écran tombent en même temps : un seul
   * message et une seule déconnexion (même anti-rafale que le 503).
   * Déconnexion FRANCHE (`logout`), pas `softLogout` : ce compte ne doit pas se
   * voir reproposer la reconnexion en surimpression de l'écran courant.
   */
  private handleAccountDisabled(message: string): void {
    if (this.accountDisabledInProgress) return;
    this.accountDisabledInProgress = true;
    this.snackService.error(message);
    this.authService.logout();
    setTimeout(() => {
      this.accountDisabledInProgress = false;
    }, 1000);
  }

  private traiteError(error: HttpErrorResponse) {
    const apiUrl = new URL(error.url || "");
    const path = apiUrl.pathname;

    // Cherche une règle correspondant à l'URL et dont la condition est vraie
    const matchingRule = this.errorRules.find(
      (rule) =>
        path.startsWith(rule.url) &&
        rule.condition(error.error.message) &&
        (!rule.status || rule.status === error.status),
    );
    if (matchingRule) {
      matchingRule.callback(error?.error.message);
    } else {
      this.snackService.error(this.resolveErrorMessage(error));
    }
  }

  extractTableName(message: string): string | null {
    const match = message.match(
      /violates foreign key constraint .* on table "(.*?)"/,
    );
    return match ? match[1] : null;
  }

  errorRules = [
    {
      url: "/api/specification_etape",
      condition: (message: string) =>
        message.includes("violates foreign key constraint"),
      callback: (message: string) => {
        const tableName = this.extractTableName(message);
        if (tableName) {
          this.snackService.info(
            `Cet élément est lié à un élément "${tableName}". Il sera archivé.`,
          );
        } else {
          this.snackService.info(
            "Cet élément est lié à un autre élément. Il ne peut pas être supprimé",
          );
        }
      },
    },
    {
      url: "/doc/api",
      status: 401,
      condition: (message: string) => {
        return message.includes("Token validation failed");
      },
      callback: (_message: string) => {
        // Avant de bouter l'utilisateur en soft-logout, on tente un refresh
        // SSO : sur les fenêtres détachées (window.open du widget supervision
        // ou de la fiche OF), la première requête doc peut tomber sur un SSO
        // qui vient juste d'expirer dans cette nouvelle session — un simple
        // refresh suffit, pas besoin de redemander le mot de passe.
        this.tryRefreshThenLogout();
      },
    },
  ];

  private async tryRefreshThenLogout(): Promise<void> {
    try {
      await this.authService.ensureRefreshSso();
      console.log("[doc 401] SSO refreshed — user kept logged in");
    } catch (e) {
      console.log("[doc 401] SSO refresh failed → soft logout", e);
      this.authService.softLogout();
    }
  }
}
